import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Plus, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import { EventDetailPanel } from '@/features/calendar/components/EventDetailPanel';
import { EventFormDialog } from '@/features/calendar/components/EventFormDialog';

interface CalendarEvent {
  id: string;
  provider: 'microsoft' | 'google';
  title: string;
  start: string;
  end: string;
  location: string;
  organizer: string;
  attendees: { name: string; email: string }[];
  is_online: boolean;
  meeting_url: string | null;
  description: string;
  account: string;
}

type ViewMode = 'week' | 'agenda';

export function CalendarPage() {
  const [view, setView] = useState<ViewMode>('week');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1 + weekOffset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [weekOffset]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    return d;
  }, [weekStart]);

  const { data, isLoading } = useQuery({
    queryKey: ['calendar-events', weekStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        `calendar-events?start=${weekStart.toISOString()}&end=${weekEnd.toISOString()}`,
        { method: 'GET' },
      );
      if (error) throw error;
      return data as { events: CalendarEvent[] };
    },
  });

  const events = data?.events ?? [];

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const days = dayNames.map((name, i) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    return { name, date, events: events.filter((e) => {
      const eDate = new Date(e.start);
      return eDate.toDateString() === date.toDateString();
    })};
  });

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      <PageHeader
        title="Calendar"
        description={`Week of ${weekStart.toLocaleDateString()}`}
        action={
          <div className="flex gap-2">
            <div className="flex rounded-md border">
              <Button variant="ghost" size="sm" onClick={() => setView('week')}
                className={cn(view === 'week' && 'bg-accent')}>
                <CalendarDays className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setView('agenda')}
                className={cn(view === 'agenda' && 'bg-accent')}>
                <List className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex rounded-md border">
              <Button variant="ghost" size="sm" onClick={() => setWeekOffset((o) => o - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekOffset((o) => o + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> New Event
            </Button>
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Calendar Grid / Agenda */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="grid grid-cols-7 gap-px p-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : view === 'week' ? (
            <div className="grid grid-cols-7 gap-px bg-border p-4">
              {days.map((day) => {
                const isToday = day.date.toDateString() === new Date().toDateString();
                return (
                  <div key={day.name} className="min-h-[120px] bg-card p-2">
                    <div className={cn(
                      'mb-1 text-xs font-medium',
                      isToday ? 'text-primary' : 'text-muted-foreground',
                    )}>
                      {day.name} {day.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {day.events.map((event) => (
                        <button
                          key={`${event.provider}-${event.id}`}
                          onClick={() => setSelectedEvent(event)}
                          className={cn(
                            'w-full rounded px-1.5 py-0.5 text-left text-xs',
                            event.provider === 'microsoft'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                          )}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          <div className="text-[10px] opacity-75">
                            {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {events.length === 0 ? (
                <EmptyState icon={CalendarDays} title="No events" description="Connect your calendar in Settings" />
              ) : (
                events.map((event) => (
                  <button
                    key={`${event.provider}-${event.id}`}
                    onClick={() => setSelectedEvent(event)}
                    className="flex w-full items-start gap-3 rounded-lg border p-3 text-left hover:bg-accent/50"
                  >
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">
                        {new Date(event.start).toLocaleDateString([], { weekday: 'short' })}
                      </div>
                      <div className="text-lg font-bold">
                        {new Date(event.start).getDate()}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{event.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {' - '}
                        {new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {event.location && ` · ${event.location}`}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Event Detail Panel */}
        {selectedEvent && (
          <div className="w-96 shrink-0 overflow-y-auto border-l">
            <EventDetailPanel
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
            />
          </div>
        )}
      </div>

      <EventFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
